import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { useCallback } from 'react';
import { rootRoute } from '@/app/root-route.tsx';
import { type RunsSearch, runsSearchSchema } from './search.ts';

const RunsListPage = lazyRouteComponent(() => import('./RunsListPage.tsx'), 'RunsListPage');
const RunDetailPage = lazyRouteComponent(() => import('./RunDetailPage.tsx'), 'RunDetailPage');

/** Demo history of autopilot runs. The live request pages are under /live/submissions. */
export const runsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/submissions',
  validateSearch: (search: Record<string, unknown>): RunsSearch => runsSearchSchema.parse(search),
  component: function RunsRouteComponent() {
    const search = runsRoute.useSearch();
    const navigate = runsRoute.useNavigate();
    const onSearchChange = useCallback(
      (patch: Partial<RunsSearch>) =>
        navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
      [navigate],
    );
    return <RunsListPage search={search} onSearchChange={onSearchChange} />;
  },
});

/** Keeps old links and bookmarks working: opens the "New submission" dialog. */
export const newRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/submissions/new',
  beforeLoad: () => {
    throw redirect({ to: '/submissions', search: { new: true }, replace: true });
  },
});

export const runRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/submissions/$runId',
  /** `wf`: the workflow id, when known, so the API reads the run directly (no visibility lag). */
  validateSearch: (search: Record<string, unknown>): { wf?: string } =>
    typeof search.wf === 'string' && search.wf ? { wf: search.wf } : {},
  component: function RunRouteComponent() {
    const { runId } = runRoute.useParams();
    const { wf } = runRoute.useSearch();
    return <RunDetailPage runId={runId} workflowId={wf} />;
  },
});
